/**
 * Login page – Cognito consumer authentication.
 */

import React, { useState, FormEvent } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Coins, Mail, Lock, ArrowRight, Clock, AlertCircle, ShieldCheck } from 'lucide-react';

export default function LoginPage() {
  const { login, completeNewPassword, error, clearError, isLoading, needsConfirmation, needsNewPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fromLocation = (location.state as { from?: { pathname: string; search: string } })?.from;
  const from = fromLocation?.pathname || '/dashboard';
  const fromSearch = fromLocation?.search || '';
  const isConnectMode = from === '/connect';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    const success = await login({ email, password });
    if (success) {
      navigate(from + fromSearch, { replace: true });
    }
  };

  const handleNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (newPassword.length < 8) {
      return; // HTML validation handles this
    }

    if (newPassword !== confirmPassword) {
      return; // Handled by UI check below
    }

    const success = await completeNewPassword(newPassword);
    if (success) {
      navigate(from + fromSearch, { replace: true });
    }
  };

  // ── New‑password challenge form ──────────────────────────────
  if (needsNewPassword) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-header">
            <ShieldCheck size={36} className="auth-logo" />
            <h1>Set New Password</h1>
            <p>Your account requires a new password before you can continue.</p>
          </div>

          {error && (
            <div className="alert alert-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleNewPassword} className="auth-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="input-icon-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  id="newPassword"
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-icon-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
            </div>

            {confirmPassword.length > 0 && newPassword !== confirmPassword && (
              <div className="alert alert-error">
                <AlertCircle size={16} />
                <span>Passwords do not match.</span>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={isLoading || newPassword.length < 8 || newPassword !== confirmPassword}
            >
              {isLoading ? 'Setting password…' : 'Set Password & Sign In'}
              {!isLoading && <ArrowRight size={18} />}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          {isConnectMode ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
              <Coins size={28} className="auth-logo" />
              <span style={{ fontSize: '20px', fontWeight: '600' }}>AI Tokens</span>
            </div>
          ) : (
            <Coins size={36} className="auth-logo" />
          )}
          <h1>Welcome Back</h1>
          <p>{isConnectMode ? 'Sign in to continue the connection' : 'Sign in to manage your AI tokens'}</p>
        </div>

        {/* Pending approval notice */}
        {needsConfirmation && (
          <div className="alert alert-warning">
            <Clock size={16} />
            <span>Your account is pending approval. An administrator will confirm your account shortly.</span>
          </div>
        )}

        {/* General error */}
        {error && !needsConfirmation && (
          <div className="alert alert-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Username / Email</label>
            <div className="input-icon-wrapper">
              <Mail size={18} className="input-icon" />
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-icon-wrapper">
              <Lock size={18} className="input-icon" />
              <input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={isLoading}>
            {isLoading ? 'Signing in…' : 'Sign In'}
            {!isLoading && <ArrowRight size={18} />}
          </button>
        </form>

        {!isConnectMode && (
          <p className="auth-footer-text">
            Don't have an account?{' '}
            <Link to="/register">Create one free</Link>
          </p>
        )}
      </div>
    </div>
  );
}
